/**
 * Le fil de la séance du tuteur (pur) : les cartes vivantes, leur vie dans la mémoire, et ce que la colonne
 * de droite montre — un seul fil, filtré sur l'élève regardé (Wajih, 02/11 au soir, tech-derisk point 9).
 *
 *  - **Les cartes** (`tutorLiveCards`) : le port de `useTutorEventsQueue` (les signaux vivants, leur instant,
 *    l'escalade, la croix retenue) et de `getEffectiveCta` (`tutorFeedAction`), plus le résumé du champion
 *    qui attend d'être lu (l'ancienne `ConversationSummaryPopup`, F-98).
 *  - **Leur vie** (`tutorLiveFeedSync`) : la durée de vie d'une carte est son signal (point 12) ; quand il
 *    tombe, la carte sort du bac et laisse une trace qui dit pourquoi (« Alice a baissé la main »). Le rendu
 *    vit tant qu'il attend validation, l'activité choisie seul 10 min (`signalEnd`).
 *  - **La colonne** (`tutorLiveFeedView`) : chaque entrée a un tronc commun (`id`, `studentId` — absent pour
 *    tout le groupe —, `at`, `kind`, `origin`). Le filtre est un paramètre, jamais une seconde liste : avec
 *    lui, les entrées de l'élève regardé et celles de tout le groupe ; sans lui (la mosaïque, ou le filtre
 *    retiré), tout — le fil du groupe n'a pas d'élève (`session.student` absent). « Ailleurs », la page le
 *    déduit des comptes de chaque élève et des cartes (`tutorSessionElsewhere`).
 *    Les instants de la colonne sont ISO (`isoOf`) : elle les écrit « 17:32 » et les range, minuit compris.
 *  - **L'historique, un niveau au-dessus** (Wajih, 04/11) : la mémoire garde aussi le mode du son et l'activité que
 *    chaque élève a devant lui, à chaque changement (`tutorLiveTimelineChanges`, des entrées `mode` et `focus` qui ne
 *    s'affichent pas) ; le ruban de la séance s'en déduit (`tutorSessionRibbon`), et les traces se rangent sous
 *    l'activité qu'il avait devant lui à leur instant (`groupHistoryByActivity`). V1 : ce que ce navigateur a vu.
 */
import { tutorFeedAction, tutorFeedPriority } from "../../../ui/formatters/tutorFeedFormatter"
import { tutorSessionTimeline } from "../../../ui/formatters/tutorSessionFormatter"
import {
    type UITutorFeedItem,
    type UITutorLiveActivity,
    type UITutorSession,
    type UITutorSessionEntry,
    type UITutorSessionRibbon,
    type UITutorSessionSetting,
    type UITutorSuggestion,
} from "../../../ui/types/tutorLive"
import { clockOf } from "../../../adapters/tutor/Live/tutorLiveFeedAdapter"
import {
    autoActivityVerb,
    ESCALATION_DELAY_MS,
    formatElapsed,
    HYDRATION_MAX_AGE_MS,
    isEscalated,
    OUT_OF_APP_DEBOUNCE_MS,
    SAM_ELIGIBLE,
    samSentKey,
    signalEnd,
    signalHash,
    SIGNAL_VERB,
    type TutorLiveSignal,
    type TutorLiveSignalType,
} from "./tutorLiveFeedRules"

/** Le signal d'une carte du fil : ceux de l'ancien panneau, et le résumé du champion. */
export type TutorLiveCardSignal = TutorLiveSignalType | "sam_summary"

/** Une carte vivante : sa carte de présentation, et ce que ses gestes lisent. */
export type TutorLiveCard = {
    /** L'empreinte (`eventHash`) : l'identité de la carte et de sa trace. */
    id: string
    signal: TutorLiveCardSignal
    studentId: string
    startedAt: number
    /** La clé de l'horloge (`signalKey`) ; absente pour un résumé. */
    key?: string
    activityId?: string
    /** La conversation d'un résumé du champion. */
    conversationId?: string
    item: UITutorFeedItem
    /** La trace de sa naissance. */
    createdText: string
}

/** Une entrée de la mémoire (`data.tutorLiveSession`), le tronc commun du point 9. */
export type TutorLiveFeedRecord = {
    id: string
    /** `mode` et `focus` : le mode du son et l'activité devant l'élève, à chaque changement (le ruban, les groupes). */
    kind: "signal" | "trace" | "tutor" | "mode" | "focus"
    studentId?: string
    at: number
    origin: "local" | "server"
    signal?: TutorLiveCardSignal
    text?: string
    /** Les réglages d'une demande de génération, que sa bulle dit d'abord (round 4). */
    settings?: UITutorSessionSetting[]
    state?: { kind: "open" } | { kind: "stale" | "decided"; at: number; text: string }
    /** Le mode du son (`dialogue`, `focused`) d'une entrée `mode`, l'activité (son id, `""` : aucune) d'une `focus`. */
    value?: string
}

export type TutorLiveStudentName = { id: string; firstname: string; avatarUrl?: string }

/** Un résumé du champion qui attend d'être lu (`liveMode.completedConversations`, non lu). */
export type TutorLiveCompletedConversation = {
    studentId: string
    conversationId: string
    at: number
    title?: string
    summary: string
    needsHelp?: boolean
    /**
     * Un quiz de prérequis réussi (`trigger_context: prerequisite_quiz`, fermé `ended`) : une information, pas une
     * décision. Il n'a pas de carte ; il laisse une trace (`tutorLiveFeedItems`). Raté, il demande le tuteur.
     */
    quizPassed?: boolean
}

export type TutorLiveCardsInput = {
    signals: TutorLiveSignal[]
    startedAt: Record<string, number>
    samSentAtByKey: Record<string, number | undefined> | null | undefined
    /** Les empreintes écartées par la croix (`dismissedEventHashes`, persistées). */
    dismissed: string[]
    completed: TutorLiveCompletedConversation[]
    students: TutorLiveStudentName[]
    /** Le statut d'une activité rendue, lu dans les listes de l'élève (`activityReviewStatus`). */
    reviewStatus: (studentId: string, activityId: string | undefined) => string | null
    dialogueWith: string | null
    isWeb: boolean
    helperName: string
    now: number
}

const SUMMARY_PROOF_MAX = 170

function nameOf(students: TutorLiveStudentName[], id: string): TutorLiveStudentName {
    return students.find((student) => student.id === id) ?? { id, firstname: "L'élève" }
}

function verbOf(signal: TutorLiveSignal, helperName: string): string {
    if (signal.type === "sam_ongoing") return `discute avec ${helperName}`
    if (signal.type === "auto_activity") return autoActivityVerb(signal.evaluation)
    return SIGNAL_VERB[signal.type]
}

function validWhileOf(signal: TutorLiveCardSignal, firstname: string): string | undefined {
    switch (signal) {
        case "hand_raised":
            return `tant que ${firstname} a la main levée`
        case "exercise_done":
            return "tant que le rendu attend ta validation"
        case "auto_activity":
            return "pendant 10 min"
        default:
            return undefined
    }
}

/**
 * Ce qui ne demande rien au masteur (weekly du 03/11, W11 : « À décider ne garde que ce qui demande une
 * action ») : l'élève discute avec le champion, l'élève a choisi seul une activité. Une trace du fil, écrite
 * une fois (`tutorLiveFeedItems`), jamais une carte du bac. Un résumé du champion l'est aussi, sauf quand
 * il demande le masteur (`needsHelp`).
 */
const INFO_SIGNALS: ReadonlySet<TutorLiveCardSignal> = new Set<TutorLiveCardSignal>(["sam_ongoing", "auto_activity"])

/** Une trace de fait, née une fois, à l'id de la carte qu'elle aurait eue (elle ne s'écrit qu'une fois). */
function traceOf(id: string, studentId: string, at: number, text: string): TutorLiveFeedRecord {
    return { id, kind: "trace", studentId, at, origin: "local", text }
}

/** Le quiz de prérequis réussi, dit : le score, quand le résumé du serveur le porte, jamais une de ses dates. */
function passedQuizText(done: TutorLiveCompletedConversation, firstname: string): string {
    // « … avec un score de 4/5 » (`QuizService::submitAttempt`).
    const score = /score de (\d+\s*\/\s*\d+)/.exec(done.summary)?.[1]
    return `${firstname} a réussi son quiz de prérequis${score != null ? ` (${score})` : ""}`
}

/**
 * Les faits du fil, triés une fois (weekly du 03/11, W11) : une **carte** pour ce qui demande une action au
 * masteur, une **trace** pour le reste (l'élève avec le champion, une activité choisie seul, un résumé qui ne
 * demande pas le tuteur, un quiz réussi). Les cartes vivantes par priorité puis la plus ancienne d'abord
 * (`sortEventsByPriority`).
 */
export function tutorLiveFeedItems(input: TutorLiveCardsInput): {
    cards: TutorLiveCard[]
    traces: TutorLiveFeedRecord[]
} {
    const dismissed = new Set(input.dismissed)
    const cards: TutorLiveCard[] = []
    const traces: TutorLiveFeedRecord[] = []
    for (const signal of input.signals) {
        const startedAt = input.startedAt[signal.key] ?? input.now
        const id = signalHash(signal.type, signal.studentId, startedAt)
        if (dismissed.has(id)) continue
        const student = nameOf(input.students, signal.studentId)
        const verb = verbOf(signal, input.helperName)
        if (INFO_SIGNALS.has(signal.type)) {
            traces.push(traceOf(id, signal.studentId, startedAt, `${student.firstname} ${verb}`))
            continue
        }
        if (signalEnd(signal, startedAt, input.now, input.reviewStatus(signal.studentId, signal.activityId)) != null)
            continue
        const samSentAt = input.samSentAtByKey?.[samSentKey(signal.type, signal.studentId)] ?? null
        const escalated = isEscalated(signal.type, startedAt, samSentAt, input.now)
        const action = tutorFeedAction({
            signal: signal.type,
            studentId: signal.studentId,
            firstname: student.firstname,
            helperName: input.helperName,
            samSent: samSentAt != null,
            escalated,
            dialogueWithStudentId: input.dialogueWith,
            isWeb: input.isWeb,
        })
        cards.push({
            id,
            signal: signal.type,
            studentId: signal.studentId,
            startedAt,
            key: signal.key,
            activityId: signal.activityId,
            createdText: `${student.firstname} ${verb}`,
            item: {
                id,
                signal: signal.type,
                priority: tutorFeedPriority(signal.type),
                student,
                text: verb,
                elapsed: formatElapsed(startedAt, input.now),
                action,
                escalated: escalated || undefined,
                sam: samSentAt != null && !escalated ? "acts" : undefined,
                // La croix : sur une carte qui demande quelque chose (`EventCard`, ni passive ni résolue).
                dismissible: action != null && action.passive !== true,
                validWhile: validWhileOf(signal.type, student.firstname),
            },
        })
    }
    for (const done of input.completed) {
        const id = `summary-${done.conversationId}`
        if (dismissed.has(id)) continue
        const student = nameOf(input.students, done.studentId)
        const title = done.title ?? "Résumé de conversation"
        const summaryText = `${input.helperName} a fini avec ${student.firstname} : ${title}`
        if (done.quizPassed === true) {
            traces.push(traceOf(id, done.studentId, done.at, passedQuizText(done, student.firstname)))
            continue
        }
        if (done.needsHelp !== true) {
            traces.push(traceOf(id, done.studentId, done.at, summaryText))
            continue
        }
        const action = tutorFeedAction({
            signal: "sam_summary",
            studentId: done.studentId,
            firstname: student.firstname,
            helperName: input.helperName,
            isWeb: input.isWeb,
        })
        const proof =
            done.summary.length > SUMMARY_PROOF_MAX ? `${done.summary.slice(0, SUMMARY_PROOF_MAX)}…` : done.summary
        cards.push({
            id,
            signal: "sam_summary",
            studentId: done.studentId,
            startedAt: done.at,
            conversationId: done.conversationId,
            createdText: summaryText,
            item: {
                id,
                signal: "sam_summary",
                priority: tutorFeedPriority("sam_summary", { needsHelp: done.needsHelp }),
                student,
                text: title,
                elapsed: formatElapsed(done.at, input.now),
                proof,
                action,
                // Fermer la popup du résumé, c'était l'avoir lu (`markConversationAsRead`).
                dismissible: true,
            },
        })
    }
    const rank = { urgent: 1, normal: 3, low: 5 } as const
    cards.sort((a, b) => rank[a.item.priority] - rank[b.item.priority] || a.startedAt - b.startedAt)
    return { cards, traces }
}

/** Les cartes vivantes seules (`tutorLiveFeedItems`). */
export function tutorLiveCards(input: TutorLiveCardsInput): TutorLiveCard[] {
    return tutorLiveFeedItems(input).cards
}

/** La trace d'une carte dont le signal est tombé : pourquoi elle sort du bac. */
export function endText(signal: TutorLiveCardSignal, firstname: string, helperName: string): string {
    switch (signal) {
        case "hand_raised":
            return `${firstname} a baissé la main`
        case "out_of_app":
            return `${firstname} est revenu dans l'appli`
        case "silence":
            return `${firstname} a repris l'échange`
        case "sam_ongoing":
            return `${firstname} a fini avec ${helperName}`
        case "exercise_done":
            return `Le rendu de ${firstname} est corrigé`
        case "auto_activity":
            return `Le choix de ${firstname} date de plus de 10 min`
        case "sam_summary":
            return `Résumé de ${firstname} lu`
    }
}

/**
 * Ce qui change dans la mémoire : les cartes nées (à écrire) et celles dont le signal est tombé (à périmer,
 * avec leur trace). Rien quand rien ne change : l'écran n'écrit qu'au changement.
 */
export function tutorLiveFeedSync(
    records: TutorLiveFeedRecord[],
    cards: TutorLiveCard[],
    names: { students: TutorLiveStudentName[]; helperName: string; now: number },
    traces: TutorLiveFeedRecord[] = []
): { opened: TutorLiveFeedRecord[]; closed: { id: string; at: number; text: string }[] } {
    const live = new Set(cards.map((card) => card.id))
    const known = new Set(records.map((record) => record.id))
    const opened = cards
        .filter((card) => !known.has(card.id))
        .map((card): TutorLiveFeedRecord => ({
            id: card.id,
            kind: "signal",
            studentId: card.studentId,
            at: card.startedAt,
            origin: "local",
            signal: card.signal,
            text: card.createdText,
            state: { kind: "open" },
        }))
    const closed = records
        .filter((record) => record.kind === "signal" && record.state?.kind === "open" && !live.has(record.id))
        .map((record) => ({
            id: record.id,
            at: names.now,
            text: endText(
                record.signal ?? "hand_raised",
                nameOf(names.students, record.studentId ?? "").firstname,
                names.helperName
            ),
        }))
    const traced = traces.filter((trace) => !known.has(trace.id))
    return { opened: traced.length > 0 ? [...opened, ...traced] : opened, closed }
}

/** Une entrée qui ne vit pas dans la mémoire : la ligne du champion (serveur), l'attente, une proposition. */
export type TutorLiveTransientEntry = { studentId?: string; at: number; entry: UITutorSessionEntry }

export type TutorLiveFeedViewInput = {
    cards: TutorLiveCard[]
    records: TutorLiveFeedRecord[]
    transient: TutorLiveTransientEntry[]
    students: TutorLiveStudentName[]
    /** L'élève regardé ; absent : la mosaïque. */
    selectedId?: string
    /** Le filtre est retiré (l'élève reste regardé, le fil montre tout). */
    filterOff: boolean
    /** Les élèves avec le champion en ce moment (`ongoingCheckpointIds`). */
    withHelper: string[]
    /** Le dernier passage du tuteur sur chaque élève (ms). */
    seen: Record<string, number>
    /** La mémoire a commencé sur cet appareil après le début du live : la colonne le dit. */
    startedHereAt?: number
    /** Une carte décidée réapparaît après 3 s si son signal vit encore (`markResolved`). */
    now: number
    /** Les activités de chaque élève (la pellicule) : le fil filtré range ses traces sous elles. Absent : rien à ranger. */
    activitiesOf?: (studentId: string) => UITutorLiveActivity[]
}

export type TutorLiveFeedView = {
    /** Toutes les cartes ouvertes, de tout le groupe (la cloche, « Ailleurs »). */
    items: UITutorFeedItem[]
    /** Le fil tel que la colonne le montre (filtré, ou tout). */
    session: UITutorSession
    /** Le fil sans filtre, que la page montre en mosaïque. */
    classSession: UITutorSession
    /** Ce qui attend une décision chez chaque élève (`countActiveEvents`) : sa vignette, la cloche. */
    pending: Record<string, number>
    /** Le filtre en cours : l'élève regardé, et s'il est appliqué. */
    filter: { studentId: string; firstname: string; on: boolean } | null
}

/** Après un geste, la carte se tait 3 s (l'ancienne carte verte) ; son signal vivant la ramène ensuite. */
const DECIDED_QUIET_MS = 3000

/** L'instant d'une entrée de la colonne (ISO) : la colonne l'écrit à l'heure et range par lui, minuit compris. */
export function isoOf(ms: number): string {
    return new Date(ms).toISOString()
}

export function tutorLiveFeedView(input: TutorLiveFeedViewInput): TutorLiveFeedView {
    const recordsById = new Map(input.records.map((record) => [record.id, record]))
    const quiet = (card: TutorLiveCard) => {
        const state = recordsById.get(card.id)?.state
        return state?.kind === "decided" && input.now - state.at < DECIDED_QUIET_MS
    }
    const openCards = input.cards.filter((card) => !quiet(card))
    const items = openCards.map((card) => card.item)
    const cardsById = new Map(openCards.map((card) => [card.id, card]))

    const pending: Record<string, number> = {}
    for (const card of openCards) {
        if (card.item.action == null || card.item.action.passive === true) continue
        pending[card.studentId] = (pending[card.studentId] ?? 0) + 1
    }

    type Scoped<T> = { studentId?: string; value: T }
    const suggestions: Scoped<UITutorSuggestion>[] = []
    const entries: Scoped<UITutorSessionEntry>[] = []
    const seenIds = new Set<string>()
    for (const record of input.records) {
        if (record.kind === "signal") {
            seenIds.add(record.id)
            const card = cardsById.get(record.id)
            const at = isoOf(record.at)
            // Ouverte en mémoire sans carte ce rendu-ci : la synchronisation la périme au prochain effet.
            const state: UITutorSuggestion["state"] =
                card == null && record.state != null && record.state.kind !== "open"
                    ? { kind: record.state.kind, at: isoOf(record.state.at), text: record.state.text }
                    : { kind: "open" }
            suggestions.push({
                studentId: record.studentId,
                value: {
                    item: card?.item ?? placeholderItem(record, input.students),
                    at,
                    createdText: record.text ?? "",
                    state,
                },
            })
        } else if (record.kind === "tutor") {
            entries.push({
                studentId: record.studentId,
                value: {
                    kind: "tutor",
                    id: record.id,
                    at: isoOf(record.at),
                    text: record.text ?? "",
                    settings: record.settings,
                },
            })
        } else if (record.kind === "trace") {
            entries.push({
                studentId: record.studentId,
                value: { kind: "trace", id: record.id, at: isoOf(record.at), text: record.text ?? "" },
            })
        }
    }
    // Une carte née ce rendu-ci, avant que la mémoire ne l'ait écrite.
    for (const card of openCards) {
        if (seenIds.has(card.id)) continue
        suggestions.push({
            studentId: card.studentId,
            value: {
                item: card.item,
                at: isoOf(card.startedAt),
                createdText: card.createdText,
                state: { kind: "open" },
            },
        })
    }
    for (const transient of input.transient) entries.push({ studentId: transient.studentId, value: transient.entry })

    const filterId = input.selectedId != null && !input.filterOff ? input.selectedId : undefined

    const build = (scopeId: string | undefined): UITutorSession => {
        const inScope = (scoped: { studentId?: string }) =>
            scopeId == null || scoped.studentId == null || scoped.studentId === scopeId
        const { history, tray } = tutorSessionTimeline({
            entries: entries.filter(inScope).map((scoped) => scoped.value),
            suggestions: suggestions.filter(inScope).map((scoped) => scoped.value),
            lastSeenAt: scopeId != null && input.seen[scopeId] != null ? isoOf(input.seen[scopeId]) : undefined,
        })
        const student = scopeId != null ? nameOf(input.students, scopeId) : undefined
        return {
            student:
                student != null
                    ? { id: student.id, firstname: student.firstname, avatarUrl: student.avatarUrl }
                    : undefined,
            history:
                scopeId != null && input.activitiesOf != null
                    ? groupHistoryByActivity(
                          history,
                          focusSegments(input.records, scopeId, input.now),
                          input.activitiesOf(scopeId)
                      )
                    : history,
            tray,
            helperActive: scopeId != null && input.withHelper.includes(scopeId) ? true : undefined,
            startedHere: input.startedHereAt != null ? isoOf(input.startedHereAt) : undefined,
        }
    }

    const selected = input.selectedId != null ? nameOf(input.students, input.selectedId) : null
    return {
        items,
        session: build(filterId),
        classSession: build(undefined),
        pending,
        filter:
            selected != null ? { studentId: selected.id, firstname: selected.firstname, on: !input.filterOff } : null,
    }
}

/** La carte d'une entrée de la mémoire dont le signal n'est plus là : sans action, elle ne se touche plus. */
function placeholderItem(record: TutorLiveFeedRecord, students: TutorLiveStudentName[]): UITutorFeedItem {
    const student = nameOf(students, record.studentId ?? "")
    const signal = record.signal ?? "hand_raised"
    return {
        id: record.id,
        signal,
        priority: tutorFeedPriority(signal),
        student,
        text: record.text ?? "",
        elapsed: clockOf(record.at),
    }
}

// ── Le prochain réveil ───────────────────────────────────────────────────────────────────────────

export type TutorLiveFeedWakeInput = {
    /** Les cartes vivantes (`tutorLiveCards`), muettes comprises. */
    cards: TutorLiveCard[]
    records: TutorLiveFeedRecord[]
    /** Le premier instant vu hors de l'appli, par élève (l'anti-rebond). */
    outOfAppSince: Record<string, number>
    samSentAtByKey: Record<string, number | undefined> | null | undefined
    now: number
}

/** Le prochain changement du temps écoulé d'une carte (`formatElapsed`) : la seconde, puis la minute. */
function nextElapsedChange(startedAt: number, now: number): number {
    const age = Math.max(0, now - startedAt)
    const step = age < 60 * 1000 ? 1000 : 60 * 1000
    return startedAt + (Math.floor(age / step) + 1) * step
}

/**
 * L'instant où le fil change sans que rien d'autre ne bouge, ou `null` : rien n'attend. L'ancien panneau
 * relisait tout à la seconde (`tick`) ; ici l'écran ne se redessine qu'à ces échéances, une à la fois :
 *  - le temps écoulé d'une carte (`item.elapsed`, `TutorSessionFeedCard`) : à la seconde tant qu'il dit
 *    « 42s » (la première minute de la carte, le seul compte à la seconde du fil), puis à la minute ;
 *  - la fin de l'anti-rebond d'une sortie de l'appli (`OUT_OF_APP_DEBOUNCE_MS`) : la carte naît ;
 *  - l'escalade d'une main ou d'un silence (`ESCALATION_DELAY_MS`, depuis l'envoi du champion s'il est parti) ;
 *  - la fin des 3 s de silence d'une carte décidée (`DECIDED_QUIET_MS`) : son signal vivant la ramène ;
 *  - l'instant d'un signal qui passe l'âge d'une autre séance (`HYDRATION_MAX_AGE_MS`), comme la prod.
 */
export function tutorLiveFeedNextWake(input: TutorLiveFeedWakeInput): number | null {
    const { now } = input
    let next = Infinity
    const consider = (at: number) => {
        if (at > now && at < next) next = at
    }
    for (const since of Object.values(input.outOfAppSince)) consider(since + OUT_OF_APP_DEBOUNCE_MS)
    const live = new Set<string>()
    for (const card of input.cards) {
        live.add(card.id)
        consider(nextElapsedChange(card.startedAt, now))
        if (card.signal === "sam_summary") continue
        if (SAM_ELIGIBLE.has(card.signal)) {
            const sentAt = input.samSentAtByKey?.[samSentKey(card.signal, card.studentId)]
            consider((sentAt ?? card.startedAt) + ESCALATION_DELAY_MS)
        }
        consider(card.startedAt + HYDRATION_MAX_AGE_MS + 1)
    }
    for (const record of input.records) {
        if (record.state?.kind === "decided" && live.has(record.id)) consider(record.state.at + DECIDED_QUIET_MS)
    }
    return next === Infinity ? null : next
}

// ── L'historique, un niveau au-dessus : le ruban et les activités ───────────────────────────────────────

/** Ce que la mémoire sait d'un élève à un instant : son mode du son et l'activité qu'il a devant lui. */
export type TutorLiveStudentNow = { studentId: string; mode: "dialogue" | "focused"; focusId: string | null }

/** La dernière valeur gardée d'un élève pour une sorte (`mode`, `focus`), ou `undefined` : jamais gardée. */
function lastValue(records: TutorLiveFeedRecord[], kind: "mode" | "focus", studentId: string): string | undefined {
    for (let i = records.length - 1; i >= 0; i--) {
        const record = records[i]
        if (record.kind === kind && record.studentId === studentId) return record.value ?? ""
    }
    return undefined
}

/**
 * Ce qu'il faut écrire dans la mémoire : un changement de mode ou d'activité devant l'élève depuis la dernière
 * valeur gardée. Rien quand rien ne change ; l'écran n'écrit qu'au changement.
 */
export function tutorLiveTimelineChanges(
    records: TutorLiveFeedRecord[],
    students: TutorLiveStudentNow[],
    now: number
): TutorLiveFeedRecord[] {
    const changes: TutorLiveFeedRecord[] = []
    for (const student of students) {
        if (lastValue(records, "mode", student.studentId) !== student.mode)
            changes.push({
                id: `mode-${student.studentId}-${now}`,
                kind: "mode",
                studentId: student.studentId,
                at: now,
                origin: "local",
                value: student.mode,
            })
        const focus = student.focusId ?? ""
        if (lastValue(records, "focus", student.studentId) !== focus)
            changes.push({
                id: `focus-${student.studentId}-${now}`,
                kind: "focus",
                studentId: student.studentId,
                at: now,
                origin: "local",
                value: focus,
            })
    }
    return changes
}

type Segment = { from: number; to: number; value: string }

/** Les plages d'une sorte pour un élève : chaque valeur gardée tient jusqu'à la suivante, la dernière jusqu'à maintenant. */
function segments(records: TutorLiveFeedRecord[], kind: "mode" | "focus", studentId: string, now: number): Segment[] {
    const mine = records
        .filter((record) => record.kind === kind && record.studentId === studentId)
        .sort((a, b) => a.at - b.at)
    return mine.map((record, index) => ({
        from: record.at,
        to: index + 1 < mine.length ? mine[index + 1].at : now,
        value: record.value ?? "",
    }))
}

const MINUTE = 60 * 1000
/** Un « rien devant lui » plus court que ceci est un rechargement ou une reconnexion, pas un moment de la séance. */
const FOCUS_BLIP = 2 * MINUTE

/**
 * Les plages d'activité d'un élève (sans les moments où il n'en avait aucune devant lui). Un trou bref entre deux
 * plages de la même activité — la salle rechargée, l'élève reconnecté, sa liste pas encore relue — ne coupe pas
 * l'activité en morceaux (vu sur la tablette : un ruban en miettes et deux groupes pour un même exercice).
 */
export function focusSegments(records: TutorLiveFeedRecord[], studentId: string, now: number): Segment[] {
    const all = segments(records, "focus", studentId, now)
    const kept: Segment[] = []
    for (const [index, segment] of all.entries()) {
        // Le dernier trou reste : il dit que l'élève n'a rien devant lui maintenant.
        const between = index < all.length - 1
        if (segment.value === "" && between && segment.to - segment.from < FOCUS_BLIP) continue
        const last = kept[kept.length - 1]
        if (last != null && last.value === segment.value) last.to = segment.to
        else kept.push({ ...segment })
    }
    return kept.filter((segment) => segment.value !== "")
}

/** L'événement du ruban que porte une entrée de la mémoire, ou rien. */
function ribbonEvent(
    record: TutorLiveFeedRecord
): Pick<UITutorSessionRibbon["events"][number], "kind" | "label"> | null {
    if (record.kind === "tutor") return { kind: "sent", label: record.text ?? "" }
    if (record.kind !== "signal") return null
    if (record.signal === "hand_raised") return { kind: "hand", label: record.text ?? "" }
    if (record.signal === "exercise_done") return { kind: "done", label: record.text ?? "" }
    if (record.signal === "sam_summary") return { kind: "helper", label: record.text ?? "" }
    return null
}

/**
 * Le ruban de la séance d'un élève, de l'ouverture de la mémoire (l'arrivée dans la salle sur ce navigateur) à
 * maintenant, en minutes : ses plages Dialogue et Concentré, les activités qu'il a eues devant lui, et les
 * événements (main levée, rendu, résumé du champion, demande du tuteur). `null` : rien encore à montrer.
 */
export function tutorSessionRibbon(input: {
    records: TutorLiveFeedRecord[]
    studentId: string
    openedAt: number
    now: number
    activities: UITutorLiveActivity[]
    clock: (ms: number) => string
}): UITutorSessionRibbon | null {
    const { records, studentId, openedAt, now } = input
    const minute = (ms: number) => Math.max(0, (ms - openedAt) / MINUTE)
    const nowMinute = minute(now)
    const modes = segments(records, "mode", studentId, now)
        .filter((segment) => segment.value === "dialogue" || segment.value === "focused")
        .map((segment) => ({
            from: minute(segment.from),
            to: minute(segment.to),
            mode: segment.value as "dialogue" | "focused",
        }))
    const titles = new Map(input.activities.map((activity) => [activity.id, activity]))
    const activities = focusSegments(records, studentId, now)
        .filter((segment) => titles.has(segment.value))
        .map((segment, index, all) => ({
            id: `${segment.value}-${segment.from}`,
            from: minute(segment.from),
            to: minute(segment.to),
            label: titles.get(segment.value)?.title ?? "",
            current: index === all.length - 1 && segment.to === now ? true : undefined,
        }))
    const events = records
        .filter((record) => record.studentId === studentId && record.at >= openedAt)
        .flatMap((record) => {
            const event = ribbonEvent(record)
            return event != null
                ? [
                      {
                          id: record.id,
                          at: minute(record.at),
                          kind: event.kind,
                          label: `${input.clock(record.at)} · ${event.label}`,
                      },
                  ]
                : []
        })
    if (modes.length === 0 && activities.length === 0 && events.length === 0) return null
    return { now: nowMinute, modes, activities, events }
}

/** Le ton du score d'une activité faite : validée, refusée, sinon neutre. */
function scoreTone(activity: UITutorLiveActivity): "success" | "danger" | "neutral" {
    if (activity.reviewStatus === "validated") return "success"
    if (activity.reviewStatus === "rejected") return "danger"
    return "neutral"
}

/** La progression d'une activité : faite, pleine ; en cours, son temps passé sur le temps prévu ; sinon rien. */
function activityProgress(activity: UITutorLiveActivity): number | undefined {
    if (activity.status === "done") return 100
    if (activity.minutes == null || activity.estimatedMinutes == null || activity.estimatedMinutes <= 0)
        return undefined
    return Math.min(100, Math.round((activity.minutes / activity.estimatedMinutes) * 100))
}

/**
 * Les traces du fil rangées sous l'activité que l'élève avait devant lui à leur instant (Wajih, 04/11 : « l'historique
 * des actions de l'élève doit monter d'un niveau visuellement ») : une suite de traces d'une même activité devient un
 * groupe (son titre, sa progression, son score, son temps passé) ; tout le reste (le champion, le tuteur, une
 * génération, le repère du dernier passage) garde sa place et son heure. Une trace hors de toute activité connue reste
 * une trace.
 */
export function groupHistoryByActivity(
    history: UITutorSessionEntry[],
    focus: Segment[],
    activities: UITutorLiveActivity[]
): UITutorSessionEntry[] {
    const known = new Map(activities.map((activity) => [activity.id, activity]))
    const activityAt = (iso: string) => {
        const at = Date.parse(iso)
        return focus.find((segment) => segment.from <= at && at < segment.to)?.value ?? null
    }
    const out: UITutorSessionEntry[] = []
    for (const entry of history) {
        const activityId = entry.kind === "trace" ? activityAt(entry.at) : null
        const activity = activityId != null ? known.get(activityId) : undefined
        if (entry.kind !== "trace" || activity == null) {
            out.push(entry)
            continue
        }
        const last = out[out.length - 1]
        const trace = { id: entry.id, at: entry.at, text: entry.text }
        if (last?.kind === "activity" && last.id.startsWith(`activity-${activity.id}-`)) {
            out[out.length - 1] = { ...last, traces: [...last.traces, trace] }
            continue
        }
        out.push({
            kind: "activity",
            id: `activity-${activity.id}-${entry.id}`,
            at: entry.at,
            title: activity.title,
            status: activity.status,
            progress: activityProgress(activity),
            score: activity.score != null ? { text: activity.score, tone: scoreTone(activity) } : undefined,
            time: activity.minutes != null ? `${activity.minutes} min` : undefined,
            traces: [trace],
        })
    }
    return out
}
